import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, of } from 'rxjs';
import { catchError, finalize, switchMap, filter, take } from 'rxjs/operators';
import { ToastController } from '@ionic/angular';
import { TokenService } from '../services/token.service';
import { ActivatedRoute } from '@angular/router';
import { GeneralService } from '../services/general.service';
import { SKIP_JWT_INTERCEPTOR } from './auth-context';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    isRefreshingToken = false;

    constructor(private generalService: GeneralService, private tokenService: TokenService, private toastCtrl: ToastController, private route: ActivatedRoute) { }
    // Intercept every HTTP call
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (request.context.get(SKIP_JWT_INTERCEPTOR) || this.isInBlockedList(request.url)) {
            return next.handle(request);
        } else {
            return next.handle(this.addToken(request)).pipe(
                catchError(err => {
                    if (err instanceof HttpErrorResponse) {
                        console.log(err.status)
                        switch (err.status) {
                            case 401:
                                return this.handle401Error(err);
                            case 403:
                                return this.handle403Error(request, next);
                            default:
                                return throwError(err);
                        }
                    } else {
                        return throwError(err);
                    }
                })
            );
        }
    }

    // Filter out URLs where you don't want to add the token!
    private isInBlockedList(url: string): Boolean {
        // Add a condition to return true if url starts with 'accounting'
        console.log(url)
        if (url.startsWith('altomateAccounting')) {
            return true;
        }

        let apiArr = ['refresh-id-token']

        let endPoint = url.split('/').pop()

        if (apiArr.find(b => b.includes(endPoint))) {
            return true;
        } else {
            return false;
        }
    }

    // Add our current access token from the service if present
    private addToken(req: HttpRequest<any>) {
        let accessToken = !this.generalService.isNativePlatform() && !this.generalService.isAltomateLink() ? localStorage.getItem('ALTOMATEPARTNER_ACCESS_TOKEN_KEY') : localStorage.getItem('ALTOMATE_ACCESS_TOKEN_KEY');
        const language = localStorage.getItem('ALTOMATE_LANGUAGE_KEY') || 'en';
        const authHeaderName = !this.generalService.isNativePlatform() && !this.generalService.isAltomateLink() ? 'Partner-Authorization' : 'Authorization';

        return req.clone({
            headers: req.headers
                .set(authHeaderName, `Bearer ${accessToken}`)
                .set('Accept-Language', language)
        });
    }

    private async handle401Error(err) {
        const toast = await this.toastCtrl.create({
            message: 'Your session is expired, Please re-login to xero!',
            duration: 2000
        });

        toast.present();
        this.tokenService.logout();
        return of(null);
    }

    // Indicates our access token is invalid, try to load a new one
    private handle403Error(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
        // Check if another call is already using the refresh logic
        if (!this.isRefreshingToken) {

            this.tokenSubject.next(null);
            this.isRefreshingToken = true;
            this.tokenService.currentAccessToken = null;
            // First, get a new access token

            return this.tokenService.refreshToken().pipe(
                switchMap((token: any) => {
                    if (token.idToken && token.refreshToken && token.success == true) {
                        // Store the new token
                        const accessToken = token.idToken;

                        const refreshToken = token.refreshToken
                        localStorage.setItem('ALTOMATE_REFRESH_TOKEN_KEY', refreshToken)

                        return this.tokenService.storeAccessToken(accessToken).pipe(
                            switchMap(_ => {
                                // Use the subject so other calls can continue with the new token
                                this.tokenSubject.next(accessToken);
                                // Perform the initial request again with the new token
                                return next.handle(this.addToken(request));
                            })
                        );
                    } else {
                        // No new token or other problem occurred
                        this.tokenService.logout()
                        return of(null);
                    }
                }),
                finalize(() => {
                    // Unblock the token reload logic when everything is done
                    this.isRefreshingToken = false;
                })
            );
        } else {
            // "Queue" other calls while we load a new token
            return this.tokenSubject.pipe(
                filter(token => token !== null),
                take(1),
                switchMap(token => {
                    // Perform the request again now that we got a new token!
                    return next.handle(this.addToken(request));
                })
            );
        }
    }

}
